
import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { registerWithEmailPassword, loginWithGoogle } from '@/lib/api/auth'
import { toast } from 'sonner'

const HoomiLogo = '/assets/hoomi-logo.png'

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4"/>
    <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
  </svg>
)

export default function Register() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true)
    setError(null)
    try {
      const response = await loginWithGoogle()
      window.location.href = response.data.url
    } catch (error: any) {
      console.error('Google signup error:', error)
      toast.error(error.message || 'Google signup failed. Please try again.')
      setError(error.message || 'Google signup failed. Please try again.')
      setIsGoogleLoading(false)
    }
  }

  const handleStep1Continue = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.email.trim()) {
      setError('Please enter your email address')
      return
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address')
      return
    }
    
    setError(null)
    setStep(2)
  }

  const handleStep2Continue = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.password) {
      setError('Please enter a password')
      return
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    setIsLoading(true)
    setError(null)
    
    try {
      await registerWithEmailPassword({
        email: formData.email,
        password: formData.password
      })
      
      toast.success('Account created successfully!')
      // Redirect to OTP verification page
      navigate('/verify-otp', { state: { email: formData.email } })
    } catch (error: any) {
      console.error('Registration error:', error)
      toast.error(error.message || 'Registration failed. Please try again.')
      setError(error.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-black flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 text-white relative">
      {/* Purple gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0"></div>

      {/* Purple accent elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-500/10 rounded-full blur-3xl"></div>

      {/* Header with logo */}
      <div className="absolute top-0 left-0 w-full p-4 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src={HoomiLogo} alt="Hoomi DePIN" width={24} height={24} />
          <span className="font-semibold text-purple-300">Hoomi Social</span>
        </Link>
      </div>

      {/* Registration form container */}
      <div className="w-full max-w-sm z-10 bg-black/30 backdrop-blur-lg rounded-xl border border-purple-500/20 shadow-xl shadow-purple-500/10 p-6">
        {step === 1 ? (
          // Step 1: Username and Email
          <form onSubmit={handleStep1Continue} className="flex flex-col gap-6">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white mb-1">First, create</h2>
              <h2 className="text-2xl font-bold text-white">your own profile</h2>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-white p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
              disabled={isLoading}
            />

            <Button 
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-12 rounded-lg font-semibold"
              disabled={isLoading}
            >
              Continue
            </Button>

            <div className="flex">
              <Button
                type="button"
                variant="outline"
                className="flex-1 bg-white/90 border-0 text-gray-900 h-12 rounded-lg hover:bg-white transition-all"
                onClick={handleGoogleSignup}
                disabled={isGoogleLoading || isLoading}
              >
                {isGoogleLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-900 border-t-transparent" />
                ) : (
                  <>
                    <GoogleIcon />
                    <span className="ml-2 text-white">Signup with Google</span>
                  </>
                )}
              </Button>
            </div>

            <button
              type="button"
              className="text-white text-sm text-center underline hover:text-purple-300 transition-colors"
              onClick={() => navigate('/auth')}
            >
              I have used Hoomi before
            </button>
          </form>
        ) : (
          // Step 2: Password
          <form onSubmit={handleStep2Continue} className="flex flex-col gap-6">
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold text-white mb-1">Next, secure</h2>
              <h2 className="text-2xl font-bold text-white">your account</h2>
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-white p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
              disabled={isLoading}
            />

            <Input
              type="password"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
              disabled={isLoading}
            />

            <div className="flex gap-3">
              <Button 
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 bg-white/10 border border-white/30 text-white h-12 rounded-lg hover:bg-white/20 transition-all"
                disabled={isLoading}
              >
                Back
              </Button>

              <Button 
                type="submit"
                className="flex-1 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-12 rounded-lg font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}